
// |--------------------------------------------------------------------------
// | KEEN UTILITY FUNCTIONS
// |--------------------------------------------------------------------------

import { accountBlacklist, userBlacklist, attrTypes, intervalMap } from '@/analytics/settings/keen-settings'
import trimEnd from 'lodash.trimend'
import moment from 'moment'


// export const datasetName = (name) => p

export const getTimeframe = (timeframeMode) => {
  return timeframeMode === 'ytd'
    ? moment().dayOfYear() > 357 && moment().week() == 1 ? 'this_52_weeks' : `this_${moment().week()}_weeks`
    : timeframeMode
}

export const getTimeframePublishedReadership = (data) => {
  const published = moment(data.published_at || data.sentAt)
  const now = moment()
  const diff = Math.max(now.diff(published, 'days') + 3, 7)

  if (diff < 31) {
    return `this_${diff}_days`
  } else if (diff < 366) {
    return `this_${Math.ceil(diff/7) + 1}_weeks`
  } else if (diff < 3653) {
    return `this_${Math.ceil(diff/30)}_months`
  } else {
    console.warn('Published date too long ago, showing past 10 years')
    return `this_120_months`
  }
}

export const getInterval = (timeframeMode) => {
  if (timeframeMode === null) {
    return null
  }

  const timeframe = getTimeframe(timeframeMode)

  const intervalRaw = trimEnd(timeframe.split('_')[2], 's')
  return intervalRaw in intervalMap ? intervalMap[intervalRaw] : null
}

export const getAttrType = (source, attr) => {
  return (source in attrTypes && attr in attrTypes[source]) ? attrTypes[source][attr] : false
}

export const buildFilters = (type = 'default') => {
  let locAccountBlacklist = accountBlacklist.slice()
  let locUserBlacklist = userBlacklist.slice()

  let filters = []

  if (type.includes('sendgrid')) {
    // WARNING: notifiable_id AND account_id are String types in stream, thus index_by type is String
    locAccountBlacklist = locAccountBlacklist.map((accountId) => accountId.toString())
    locUserBlacklist = locUserBlacklist.map((userId) => userId.toString())

    defaultFilters(filters, ['custom_attributes.alertable_id', 'custom_attributes.alertable_type', 'custom_attributes.notifiable_id'])

    if (type.includes('article')) {
      filters.push({ 'operator': 'contains', 'property_name': 'custom_attributes.alertable_type', 'property_value': 'Article' })
    } else if (type.includes('newsletter')) {
      filters.push({'operator': 'contains', 'property_name': 'custom_attributes.alertable_type', 'property_value': 'Newsletter'})
    }
  } else {
    defaultNumberFilters(filters, ['custom_attributes.alertable_id', 'custom_attributes.user_id'])

    // if (!type.includes('newsletter')) {
    //   defaultNumberFilters(filters, ['custom_attributes.channel_id'])
    // }

    const customFilters = [
      // Account_id was added later, thus might not exist on some events, but we want to include these events regardless. Disabled because this now has equal function to the null filter below.
      // {'operator': 'ne', 'property_name': 'custom_attributes.account_id', 'property_value': ''},

      // Update April 2020: From now on we want want to display all user activity, even if they do not belong to an account
      // {'operator': 'ne', 'property_name': 'custom_attributes.account_id', 'property_value': null},

      // Ensure article viewers had access to entire text OR property doesn't exist:
      // ('ne' == either does not exist, or exists and is not equal to)
      // ('eq' == exists and is equal to)
      // OR operand was added to fix a bug on 2021_01_13: Paywall as set incorrectly to true for all events.
      // This fix results in an approximation of total events, with a slight inflation of total events due to legit paywall=true events being accepted for this day

      {
        'operator': 'or', 'operands': [
          { 'operator': 'ne', 'property_name': 'custom_attributes.paywall', 'property_value': true },
          { 'operator': 'contains', 'property_name': 'local_time_full', 'property_value': "2021-01-1" },
        ]
      }
    ]

    filters = [...filters, ...customFilters]
  }

  if (type === 'analyst') {
    defaultNumberFilters(filters, ['custom_attributes.primary_analyst_id'])
  }

  if (type === 'company') {
    defaultNumberFilters(filters, ['custom_attributes.company_id'])
  }

  if (type === 'geo-country') {
    defaultFilters(filters, ['geo.country'])
  }

  // ONLY SET THIS TYPE WHEN PAGE IS ACCOUNT OR USER ANALYTICS
  if (!type.includes('account') && !type.includes('user')) {
    for (const accountId of locAccountBlacklist) {
      filters.push({'operator': 'ne', 'property_name': 'custom_attributes.account_id', 'property_value': accountId})
    }

    for (const userId of locUserBlacklist) {
      filters.push({'operator': 'ne', 'property_name': type.includes('sendgrid') ? 'custom_attributes.notifiable_id' : 'custom_attributes.user_id', 'property_value': userId})
    }

  }

  return filters
}

const defaultNumberFilters = (filters, attrs) => {
  for (const attr of attrs) {
    // Ensure attribute is correct number
    filters.push({'operator': 'gt', 'property_name': attr, 'property_value': -1})
  }

  defaultFilters(filters, attrs)

  return filters
}

const defaultFilters = (filters, attrs) => {
  for (const attr of attrs) {
    // Ensure attribute exists
    filters.push({'operator': 'exists', 'property_name': attr, 'property_value': true})

    // Ensure attribute is set to non-empty value if exists
    filters.push({'operator': 'ne', 'property_name': attr, 'property_value': ''})

    // Ensure attribute is not null if exists
    filters.push({'operator': 'ne', 'property_name': attr, 'property_value': null})
  }

  return filters
}
